import { forwardRef, type HTMLAttributes } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * Spinner sizes
 */
type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Spinner variants
 */
type SpinnerVariant = 'default' | 'glass' | 'dots' | 'pulse' | 'gradient';

/**
 * Props for the Spinner component
 */
export interface SpinnerProps extends HTMLAttributes<HTMLDivElement> {
  /** Size of the spinner */
  size?: SpinnerSize;
  /** Visual variant */
  variant?: SpinnerVariant;
  /** Custom color (CSS color value) */
  color?: string;
  /** Label for screen readers */
  label?: string;
  /** Show label visually */
  showLabel?: boolean;
}

/**
 * Size configurations
 */
const sizeConfig: Record<SpinnerSize, { container: string; spinner: string; dot: string; label: string }> = {
  xs: {
    container: 'w-4 h-4',
    spinner: 'w-4 h-4 border-2',
    dot: 'w-1 h-1',
    label: 'text-xs',
  },
  sm: {
    container: 'w-6 h-6',
    spinner: 'w-6 h-6 border-2',
    dot: 'w-1.5 h-1.5',
    label: 'text-sm',
  },
  md: {
    container: 'w-8 h-8',
    spinner: 'w-8 h-8 border-3',
    dot: 'w-2 h-2',
    label: 'text-base',
  },
  lg: {
    container: 'w-12 h-12',
    spinner: 'w-12 h-12 border-4',
    dot: 'w-2.5 h-2.5',
    label: 'text-lg',
  },
  xl: {
    container: 'w-16 h-16',
    spinner: 'w-16 h-16 border-4',
    dot: 'w-3 h-3',
    label: 'text-xl',
  },
};

/**
 * Default spinner with rotating ring
 */
const DefaultSpinner = ({
  size,
  color = 'var(--color-primary)',
}: {
  size: SpinnerSize;
  color?: string;
}) => (
  <motion.div
    className={cn(
      sizeConfig[size].spinner,
      'rounded-full',
      'border-t-transparent'
    )}
    style={{
      borderColor: `color-mix(in srgb, ${color} 19%, transparent)`,
      borderTopColor: color,
    }}
    animate={{ rotate: 360 }}
    transition={{
      duration: 1,
      repeat: Infinity,
      ease: 'linear',
    }}
  />
);

/**
 * Glass spinner with blur effect
 */
const GlassSpinner = ({
  size,
  color = 'var(--color-primary)',
}: {
  size: SpinnerSize;
  color?: string;
}) => (
  <div className={cn('relative', sizeConfig[size].container)}>
    {/* Outer glass ring */}
    <motion.div
      className={cn(
        'absolute inset-0 rounded-full',
        'backdrop-blur-md',
        'bg-white/[0.08]',
        'border border-white/[0.15]'
      )}
      animate={{
        scale: [1, 1.1, 1],
        opacity: [0.5, 0.8, 0.5],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />

    {/* Inner rotating gradient */}
    <motion.div
      className={cn('absolute inset-1 rounded-full')}
      style={{
        background: `conic-gradient(from 0deg, transparent, ${color}, transparent)`,
      }}
      animate={{ rotate: 360 }}
      transition={{
        duration: 1.2,
        repeat: Infinity,
        ease: 'linear',
      }}
    />

    {/* Center blur */}
    <div
      className={cn(
        'absolute inset-2 rounded-full',
        'backdrop-blur-xl',
        'bg-surface-100/80'
      )}
    />
  </div>
);

/**
 * Dots spinner
 */
const DotsSpinner = ({
  size,
  color = 'var(--color-primary)',
}: {
  size: SpinnerSize;
  color?: string;
}) => {
  const dots = [0, 1, 2];

  return (
    <div className="flex items-center gap-1">
      {dots.map((i) => (
        <motion.div
          key={i}
          className={cn(sizeConfig[size].dot, 'rounded-full')}
          style={{ backgroundColor: color }}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            delay: i * 0.15,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
};

/**
 * Pulse spinner
 */
const PulseSpinner = ({
  size,
  color = 'var(--color-primary)',
}: {
  size: SpinnerSize;
  color?: string;
}) => (
  <div className={cn('relative', sizeConfig[size].container)}>
    {/* Outer pulse */}
    <motion.div
      className={cn('absolute inset-0 rounded-full')}
      style={{ backgroundColor: color }}
      animate={{
        scale: [1, 2],
        opacity: [0.4, 0],
      }}
      transition={{
        duration: 1.2,
        repeat: Infinity,
        ease: 'easeOut',
      }}
    />

    {/* Middle pulse */}
    <motion.div
      className={cn('absolute inset-0 rounded-full')}
      style={{ backgroundColor: color }}
      animate={{
        scale: [1, 1.5],
        opacity: [0.6, 0],
      }}
      transition={{
        duration: 1.2,
        repeat: Infinity,
        delay: 0.2,
        ease: 'easeOut',
      }}
    />

    {/* Center dot */}
    <motion.div
      className={cn('absolute inset-0 m-auto rounded-full')}
      style={{
        backgroundColor: color,
        width: '40%',
        height: '40%',
      }}
      animate={{
        scale: [0.8, 1, 0.8],
      }}
      transition={{
        duration: 1.2,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  </div>
);

/**
 * Spinner component with Liquid Glass design
 *
 * @example
 * ```tsx
 * <Spinner size="md" variant="glass" />
 *
 * <Spinner size="lg" variant="dots" label="Loading data..." showLabel />
 *
 * <Spinner size="sm" color="var(--color-success)" />
 * ```
 */
export const Spinner = forwardRef<HTMLDivElement, SpinnerProps>(
  (
    {
      size = 'md',
      variant = 'default',
      color,
      label = 'Loading',
      showLabel = false,
      className,
      ...props
    },
    ref
  ) => {
    const spinnerComponents: Record<SpinnerVariant, typeof DefaultSpinner> = {
      default: DefaultSpinner,
      glass: GlassSpinner,
      gradient: GlassSpinner,
      dots: DotsSpinner,
      pulse: PulseSpinner,
    };

    const SpinnerComponent = spinnerComponents[variant];

    return (
      <div
        ref={ref}
        role="status"
        aria-label={label}
        className={cn(
          'inline-flex flex-col items-center gap-2',
          className
        )}
        {...props}
      >
        <SpinnerComponent size={size} color={color} />

        {showLabel && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={cn(
              sizeConfig[size].label,
              'text-surface-500'
            )}
          >
            {label}
          </motion.span>
        )}

        {/* Hidden label for screen readers */}
        {!showLabel && <span className="sr-only">{label}</span>}
      </div>
    );
  }
);

Spinner.displayName = 'Spinner';

export default Spinner;
